import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Linkedin,
  Instagram,
  Facebook,
  Mail,
  Phone,
  MapPin,
  ArrowRight,
  Clock
} from 'lucide-react';
import image from "../assets/image.png"

const Footer = () => {
  const services = [
    { name: 'Business Intelligence', path: '/services/business-intelligence' },
    { name: 'Cloud Migration', path: '/services/cloud-migration' },
    { name: 'Knowledge Process Outsourcing', path: '/services/data-engineering' },
    { name: 'Deltek Costpoint', path: '/services/deltek-costpoint' },
    { name: 'Yardi Services', path: '/services/yardi-property-management-software' },
    { name: 'Software Development', path: '/services/software-development' },
  ];

  const quickLinks = [
    { name: 'About Us', path: '/about' },
    { name: 'Careers', path: '/careers' },
    { name: 'Blog', path: '/blog' },
    { name: 'Testimonials', path: '/testimonials' },
    { name: 'Contact', path: '/contact' },
  ];

  return (
    <footer className="bg-navy text-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Main Footer Content */}
        <div className="py-16">
          <div className="grid lg:grid-cols-4 md:grid-cols-2 gap-8">
            {/* Company Info */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="lg:col-span-1"
            >
              <Link to="/" className="flex items-center space-x-2 mb-28">
                <motion.div
                  whileHover={{ scale: 1.05 }}
                  className="flex items-center space-x-2"
                >
                  <img
                    src={image}
                    alt="Revolve  Logo"
                    className="h-14 w-auto object-contain"
                    style={{ maxWidth: '120px' }}
                  />
                </motion.div>
              </Link>
              {/* 
              <p className="text-gray-300 mb-6 leading-relaxed">
                Transforming businesses through innovative software solutions
                with 40+ experts and 5+ decades of experience.
              </p> */}

              {/* Social Media */}
              <div className="flex space-x-4">
                <a
                  href="https://www.linkedin.com/company/revolve-solutions-private-limited/ "
                  className="p-2 bg-white/10 rounded-lg hover:bg-blue-600 transition-colors duration-300"
                >
                  <Linkedin className="w-5 h-5" />
                </a>
                <a
                  href="https://www.instagram.com/revolve_solutions_pvt_ltd/"
                  className="p-2 bg-white/10 rounded-lg hover:bg-blue-600 transition-colors duration-300"
                >
                  <Instagram className="w-5 h-5" />
                </a>
                <a
                  href="https://www.facebook.com/people/Revolve-Solution-Private-Limited/100093308727397"
                  className="p-2 bg-white/10 rounded-lg hover:bg-blue-600 transition-colors duration-300"
                >
                  <Facebook className="w-5 h-5" />
                </a>
              </div>
            </motion.div>

            {/* Services */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.1 }}
            >
              <h3 className="text-lg font-semibold mb-6">Our Services</h3>
              <ul className="space-y-3">
                {services.map((service) => (
                  <li key={service.path}>
                    <Link
                      to={service.path}
                      className="text-gray-300 hover:text-white transition-colors duration-300 flex items-center group"
                    >
                      <span className="group-hover:translate-x-1 transition-transform duration-300">
                        {service.name}
                      </span>
                    </Link>
                  </li>
                ))}
              </ul>
            </motion.div>

            {/* Quick Links */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              <h3 className="text-lg font-semibold mb-6">Quick Links</h3>
              <ul className="space-y-3">
                {quickLinks.map((link) => (
                  <li key={link.path}>
                    <Link
                      to={link.path}
                      className="text-gray-300 hover:text-white transition-colors duration-300 flex items-center group"
                    >
                      <span className="group-hover:translate-x-1 transition-transform duration-300">
                        {link.name}
                      </span>
                    </Link>
                  </li>
                ))}
              </ul>
            </motion.div>

            {/* Contact Info */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.3 }}
            >
              <h3 className="text-lg font-semibold mb-6">Get in Touch</h3>

              <div className="space-y-4">
                <div className="flex items-start space-x-3">
                  <Mail className="w-5 h-5 text-blue-400 mt-1 flex-shrink-0" />
                  <div>
                    <p className="text-gray-300">Email</p>
                    <a
                      href="mailto:contact@revolvespl.com"
                      className="text-white hover:text-blue-400 transition-colors"
                    >
                      contact@revolvespl.com
                    </a>
                  </div>
                </div>

                <div className="flex items-start space-x-3">
                  <MapPin className="w-5 h-5 text-blue-400 mt-1 flex-shrink-0" />
                  <div>
                    <p className="text-gray-300">Address</p>
                    <p className="text-white">
                      Ashburn, VA 20147<br />
                    </p>
                  </div>
                </div>

                {/* Time Zone */}
                <div className="flex items-start space-x-3">
                  <Clock className="w-5 h-5 text-blue-400 mt-1 flex-shrink-0" />
                  <div>
                    <p className="text-gray-300">Time Zone</p>
                    <p className="text-white">
                      EST (Eastern Standard Time)
                    </p>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>

        {/* Bottom Bar */}
        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.4 }}
          className="border-t border-white/10 py-2"
        >
          <div className="flex flex-col md:flex-row justify-center items-center space-y-4 md:space-y-0">
            <p className="text-gray-300 text-center md:text-left">
              Copyright {new Date().getFullYear()} ©
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-cyan-400 font-semibold mx-1">
                Revolve
              </span>
              . All rights reserved. Crafted By
              <a
                href="https://www.ariesinnovation.com/"
                className="text-transparent bg-clip-text bg-gradient-to-r from-orange-400 to-yellow-400 hover:from-orange-300 hover:to-yellow-300 transition-all duration-300 font-semibold ml-1"
              >
                Aries Innovation
              </a>
            </p>
          </div>
        </motion.div>
      </div>
    </footer>
  );
};

export default Footer;